import { isImageGone } from '@components/Customization/reportImageStatus';
import { useGetReportDesignImageQuery } from '@store/api/reportDesignEndpoints';
import { useEffect } from 'react';
import { type DesignImages } from './ImageField';

/** How one uploaded image of a design stands for whoever shows it. */
export interface DesignPicture {
  /** An address the image can be shown from, once there is one. */
  picture: string | undefined;
  /** The server has answered that it holds no such image, to this read or to an earlier request. */
  gone: boolean;
  reading: boolean;
  /** What the read failed with, if it did. */
  error: unknown;
}

/**
 * One uploaded image of a design as a picture: the file itself while it was picked during
 * this visit, otherwise read from the server, which hands an image only to a request that
 * carries the session. The inspector's control and the canvas both show an image through
 * this, so the two cannot disagree about it, and it is read once for both.
 *
 * An image the server no longer has is told to the editor, which lists it as something to
 * fix and holds the save back.
 */
export const useDesignPicture = (images: DesignImages, imageId: string | undefined): DesignPicture => {
  const local = imageId ? images.local[imageId] : undefined;

  // `currentData`, not `data`: after the image is replaced, `data` is still the old one's address.
  const { currentData: stored, error, isFetching } = useGetReportDesignImageQuery(
    { designId: images.designId, imageId: imageId ?? '' },
    { skip: !imageId || Boolean(local) },
  );

  const notFound = Boolean(imageId) && isImageGone(error);
  const { reportMissing } = images;

  useEffect(() => {
    if (imageId && notFound) reportMissing(imageId);
  }, [imageId, notFound, reportMissing]);

  return {
    picture: local ?? stored,
    gone: notFound || Boolean(imageId && images.missing.has(imageId)),
    reading: isFetching,
    error,
  };
};
